import React, { useState, useEffect, useRef } from 'react';
import { useUser } from './UserContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Card } from './ui/card';
import { AspectRatio } from './ui/aspect-ratio';
import { toast } from './ui/use-toast';
import { fetchWithRetry } from '../utils/tourUtils';
import { getImageUrls, extractTimestamp } from '../utils/imageUtils';
import { BUCKET_NAME_PHOTOS, FUNCTION_NAMES } from '../constants';
import { API_BASE_URL } from '../config';

const BATCH_SIZE = 30; // Number of thumbnails to load at once

const FileManager = () => {
  const { user } = useUser();
  const [allImageKeys, setAllImageKeys] = useState([]);
  const [thumbnailsUrls, setThumbnailsUrls] = useState({});
  const [assignedImageKeys, setAssignedImageKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImages, setSelectedImages] = useState(new Set());
  const [filter, setFilter] = useState({
    user: null,
    project: null,
    assigned: 'all', // 'all', 'assigned', 'unassigned'
  });
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loadingProgress, setLoadingProgress] = useState({ loaded: 0, total: 0 });
  const [isLoadingThumbnails, setIsLoadingThumbnails] = useState(false);
  const [tourImages, setTourImages] = useState([]); // Store full tour images data
  const loadingRef = useRef(false); // Add ref to track loading state
  const imageCache = useRef(new Map()); // Cache for image data
  const [loadingQueue, setLoadingQueue] = useState([]);
  const loadingControllerRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [s3KeysRes, tourImagesRes, usersRes, projectsRes] = await Promise.all([
        fetchWithRetry({
          url: `${API_BASE_URL}/api/post`,
          data: {
            function_name: FUNCTION_NAMES.POST.LIST_S3_KEYS,
            payload: {
              bucketName: BUCKET_NAME_PHOTOS,
              prefixPath: 'raw/images/',
            },
          },
          label: 'image keys from S3',
        }),
        fetchWithRetry({
          url: `${API_BASE_URL}/api/post`,
          data: {
            function_name: FUNCTION_NAMES.PRISMA,
            operation: 'tourImage.findMany',
            payload: {
              select: {
                imageKey: true,
                tour: {
                  select: {
                    id: true,
                    projectId: true,
                    name: true,
                  }
                },
              },
            },
          },
          label: 'assigned tour images',
        }),
        fetchWithRetry({
          url: `${API_BASE_URL}/api/post`,
          data: {
            function_name: FUNCTION_NAMES.PRISMA,
            operation: 'user.findMany',
            payload: {},
          },
          label: 'users',
        }),
        fetchWithRetry({
          url: `${API_BASE_URL}/api/post`,
          data: {
            function_name: FUNCTION_NAMES.PRISMA,
            operation: 'project.findMany',
            payload: {
              include: {
                project: true, // Include project data
              },
            },
          },
          label: 'projects',
        }),
      ]);

      setAllImageKeys(s3KeysRes);
      setTourImages(tourImagesRes);
      debugger;
      setAssignedImageKeys(tourImagesRes.map(item => item.imageKey));
      setUsers(usersRes);
      setProjects(projectsRes);
      setLoadingProgress({ loaded: 0, total: s3KeysRes.length });

      // Start loading thumbnails in batches
      const thumbnailsImageKeys = s3KeysRes.map(item =>
        item.Key.replace('/images/', '/thumbnails/')
      );
      loadThumbnailsInBatches(thumbnailsImageKeys);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Function to convert image URL to base64
  const getBase64FromUrl = async url => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Error converting image to base64:', error);
      return url; // Return original URL if conversion fails
    }
  };

  // Function to load and cache image
  const loadAndCacheImage = async (key, url) => {
    if (imageCache.current.has(key)) {
      return imageCache.current.get(key);
    }

    try {
      const base64 = await getBase64FromUrl(url);
      imageCache.current.set(key, base64);
      return base64;
    } catch (error) {
      console.error('Error caching image:', error);
      return url; // Return original URL if caching fails
    }
  };

  const loadThumbnailsInBatches = async thumbnailsImageKeys => {
    // Cancel any ongoing loading
    if (loadingControllerRef.current) {
      loadingControllerRef.current.abort();
    }
    loadingControllerRef.current = new AbortController();

    if (loadingRef.current) {
      console.log('Thumbnail loading already in progress, skipping...');
      return;
    }

    loadingRef.current = true;
    setIsLoadingThumbnails(true);
    try {
      const totalKeys = thumbnailsImageKeys.length;
      const alreadyLoadedCount = Object.keys(thumbnailsUrls).length;
      setLoadingProgress({ loaded: alreadyLoadedCount, total: totalKeys });

      // Get all keys that need to be loaded
      const remainingKeys = thumbnailsImageKeys.filter(key => !thumbnailsUrls[key]);

      // If there are filters applied, get filtered keys
      const filteredKeys =
        filter.user !== 'all' || filter.project !== 'all' || filter.assigned !== 'all'
          ? filteredImageKeys.map(image => image.Key.replace('/images/', '/thumbnails/'))
          : [];

      // Create a priority queue for loading
      const priorityQueue = [];

      // Add filtered keys to priority queue if they exist
      if (filteredKeys.length > 0) {
        const filteredKeysToLoad = filteredKeys.filter(key => {
          const originalKey = key.replace('/thumbnails/', '/images/');
          // Check both thumbnailsUrls and imageCache
          return !thumbnailsUrls[originalKey] && !imageCache.current.has(originalKey);
        });
        priorityQueue.push(...filteredKeysToLoad);
      }

      // Add remaining keys to the queue
      const remainingKeysToLoad = remainingKeys.filter(key => {
        // Check both thumbnailsUrls and imageCache
        return !priorityQueue.includes(key) && !imageCache.current.has(key);
      });
      priorityQueue.push(...remainingKeysToLoad);

      // If nothing to load, we're done
      if (priorityQueue.length === 0) {
        setLoadingProgress({ loaded: totalKeys, total: totalKeys });
        return;
      }

      // Process the queue in batches
      for (let i = 0; i < priorityQueue.length; i += BATCH_SIZE) {
        if (!loadingRef.current || loadingControllerRef.current.signal.aborted) {
          console.log('Loading stopped due to filter change or cancellation');
          return;
        }

        const batch = priorityQueue.slice(i, i + BATCH_SIZE);

        try {
          const thumbnailsUrlsFromS3 = await getImageUrls(batch);

          // Load and cache batch images
          const cachedUrls = await Promise.all(
            thumbnailsUrlsFromS3.map(async ({ key, url }) => {
              if (loadingControllerRef.current.signal.aborted) {
                throw new Error('Loading cancelled');
              }
              const originalKey = key.replace('/thumbnails/', '/images/');

              // Check if already in cache
              if (imageCache.current.has(originalKey)) {
                return { key: originalKey, url: imageCache.current.get(originalKey) };
              }

              const cachedUrl = await loadAndCacheImage(originalKey, url);
              return { key: originalKey, url: cachedUrl };
            })
          );

          setThumbnailsUrls(prev => {
            const newUrls = { ...prev };
            cachedUrls.forEach(({ key, url }) => {
              newUrls[key] = url;
            });
            return newUrls;
          });

          const currentBatchSize = batch.length;
          setLoadingProgress(prev => ({
            loaded: Math.min(prev.loaded + currentBatchSize, totalKeys),
            total: totalKeys,
          }));

          // Small delay between batches to prevent overwhelming the browser
          await new Promise(resolve => setTimeout(resolve, 50));
        } catch (error) {
          if (error.name === 'AbortError') {
            console.log('Loading batch was cancelled');
            return;
          }
          throw error;
        }
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        // toast({ title: 'Error', description: 'Failed to load thumbnails', variant: 'destructive' });
      }
    } finally {
      loadingRef.current = false;
      setIsLoadingThumbnails(false);
      loadingControllerRef.current = null;
    }
  };

  // Add effect to reload thumbnails when filters change
  useEffect(() => {
    if (allImageKeys.length > 0) {
      // Stop any ongoing thumbnail loading
      loadingRef.current = false;
      if (loadingControllerRef.current) {
        loadingControllerRef.current.abort();
      }

      // Small delay to ensure previous loading is stopped
      setTimeout(() => {
        const thumbnailsImageKeys = allImageKeys.map(item =>
          item.Key.replace('/images/', '/thumbnails/')
        );
        loadThumbnailsInBatches(thumbnailsImageKeys);
      }, 100);
    }
  }, [filter, allImageKeys]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      loadingRef.current = false;
      if (loadingControllerRef.current) {
        loadingControllerRef.current.abort();
      }
    };
  }, []);

  const filteredImageKeys = React.useMemo(() => {
    return allImageKeys
      .filter(item => {
        // User filter
        const isFromUser =
          filter.user === 'all' ||
          !filter.user ||
          item.Key.startsWith(`raw/images/${filter.user}/`);

        // Assignment status filter
        const isAssigned = assignedImageKeys.includes(item.Key);
        const matchesAssignment =
          filter.assigned === 'all' ||
          (filter.assigned === 'assigned' && isAssigned) ||
          (filter.assigned === 'unassigned' && !isAssigned);

        // Project filter
        const matchesProject =
          filter.project === 'all' ||
          !filter.project ||
          tourImages.some(
            tourImage =>
              tourImage.imageKey === item.Key && tourImage.tour?.projectId === filter.project
          );

        return isFromUser && matchesAssignment && matchesProject;
      })
      .map(item => {
        const fileName = item.Key.split('/').pop();
        const { date, time } = extractTimestamp(fileName);
        return {
          ...item,
          timestamp: { date, time },
          dateString: `${date.slice(6)}-${date.slice(3, 5)}-${date.slice(0, 2)}T${time}`,
        };
      });
  }, [allImageKeys, assignedImageKeys, filter, tourImages]);

  const handleImageSelect = imageKey => {
    setSelectedImages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(imageKey)) {
        newSet.delete(imageKey);
      } else {
        newSet.add(imageKey);
      }
      return newSet;
    });
  };

  const handleBulkUnassign = async () => {
    if (selectedImages.size === 0) return;

    try {
      // First get all tour image IDs for the selected image keys
      const tourImagesToDelete = tourImages.filter(tourImage =>
        selectedImages.has(tourImage.imageKey)
      );

      // Process in batches of 30
      const BATCH_SIZE = 30;
      for (let i = 0; i < tourImagesToDelete.length; i += BATCH_SIZE) {
        const batch = tourImagesToDelete.slice(i, i + BATCH_SIZE);

        // Delete each tour image in the batch
        await Promise.all(
          batch.map(tourImage =>
            fetchWithRetry({
              url: `${API_BASE_URL}/api/post`,
              data: {
                function_name: FUNCTION_NAMES.PRISMA,
                operation: 'tourImage.delete',
                payload: {
                  id: tourImage.id,
                },
              },
              label: `unassign image ${tourImage.id}`,
            })
          )
        );
      }

      toast({
        title: 'Success',
        description: 'Images unassigned successfully',
      });
      setSelectedImages(new Set());
      fetchData();
    } catch (error) {
      console.error('Error unassigning images:', error);
      toast({
        title: 'Error',
        description: 'Failed to unassign images',
        variant: 'destructive',
      });
    }
  };

  const handleBulkDelete = async () => {
    if (selectedImages.size === 0) return;

    if (
      !window.confirm('Are you sure you want to delete these images? This action cannot be undone.')
    ) {
      return;
    }

    try {
      const selectedKeysArray = Array.from(selectedImages);
      const BATCH_SIZE = 50;

      // Process S3 deletions in batches of 50
      for (let i = 0; i < selectedKeysArray.length; i += BATCH_SIZE) {
        const batch = selectedKeysArray.slice(i, i + BATCH_SIZE);

        await fetchWithRetry({
          url: `${API_BASE_URL}/api/post`,
          data: {
            function_name: FUNCTION_NAMES.POST.DELETE_S3_KEY,
            payload: {
              batchKeys: batch,
              targetBucket: BUCKET_NAME_PHOTOS,
            },
          },
          label: `delete images batch ${i / BATCH_SIZE + 1}`,
        });
      }

      toast({
        title: 'Success',
        description: 'Images deleted successfully',
      });
      setSelectedImages(new Set());
      fetchData();
    } catch (error) {
      console.error('Error deleting images:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete images',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-4">File Manager</h1>
      {filteredImageKeys.length} Current Selection
      {/* Loading Progress */}
      {isLoadingThumbnails && (
        <div className="mb-4">
          <p className="text-sm text-gray-600">
            Loading thumbnails: {loadingProgress.loaded} of {loadingProgress.total}
          </p>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div
              className="bg-blue-600 h-2.5 rounded-full"
              style={{ width: `${(loadingProgress.loaded / loadingProgress.total) * 100}%` }}
            ></div>
          </div>
        </div>
      )}
      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <Select
          value={filter.user}
          onValueChange={value => setFilter(prev => ({ ...prev, user: value }))}
        >
          <SelectTrigger>
            <SelectValue placeholder="Filter by user" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Users</SelectItem>
            {users.map(user => (
              <SelectItem key={user.id} value={user.id}>
                {user.email}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={filter.project}
          onValueChange={value => setFilter(prev => ({ ...prev, project: value }))}
        >
          <SelectTrigger>
            <SelectValue placeholder="Filter by project" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Projects</SelectItem>
            {projects.map(project => (
              <SelectItem key={project.id} value={project.id}>
                {project.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={filter.assigned}
          onValueChange={value => setFilter(prev => ({ ...prev, assigned: value }))}
        >
          <SelectTrigger>
            <SelectValue placeholder="Filter by assignment" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Images</SelectItem>
            <SelectItem value="assigned">Assigned Only</SelectItem>
            <SelectItem value="unassigned">Unassigned Only</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {/* Selection Info */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <Checkbox
            id="select-all"
            checked={
              selectedImages.size === filteredImageKeys.length && filteredImageKeys.length > 0
            }
            onCheckedChange={checked => {
              if (checked) {
                // Select all images from current filtered view
                setSelectedImages(new Set(filteredImageKeys.map(image => image.Key)));
              } else {
                // Deselect all
                setSelectedImages(new Set());
              }
            }}
          />
          <label htmlFor="select-all" className="text-sm text-gray-600">
            Select All ({filteredImageKeys.length} photos)
          </label>
          {filter.user !== 'all' && filter.user && (
            <span className="ml-2">• User: {users.find(u => u.id === filter.user)?.email}</span>
          )}
          {filter.project !== 'all' && filter.project && (
            <span className="ml-2">
              • Project: {projects.find(p => p.id === filter.project)?.name}
            </span>
          )}
          {filter.assigned !== 'all' && (
            <span className="ml-2">
              • {filter.assigned === 'assigned' ? 'Assigned' : 'Unassigned'} only
            </span>
          )}
        </div>
        {selectedImages.size > 0 && (
          <div className="text-sm text-blue-600">{selectedImages.size} photos selected</div>
        )}
      </div>
      {/* Bulk Actions */}
      {selectedImages.size > 0 && (
        <div className="flex gap-2 mb-4">
          <Button onClick={handleBulkUnassign} variant="outline">
            Unassign Selected ({selectedImages.size})
          </Button>
          <Button onClick={handleBulkDelete} variant="destructive">
            Delete Selected ({selectedImages.size})
          </Button>
        </div>
      )}
      {/* Image Grid */}
      {loading ? (
        <div>Loading initial data...</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredImageKeys.map(image => (
            <Card
              key={image.Key}
              className="relative cursor-pointer hover:shadow-md transition-shadow"
              onClick={e => {
                // Only handle click if it's not on the checkbox
                if (!e.target.closest('.checkbox-container')) {
                  handleImageSelect(image.Key);
                }
              }}
            >
              <div className="absolute top-2 left-2 z-10 checkbox-container">
                <Checkbox
                  checked={selectedImages.has(image.Key)}
                  onCheckedChange={() => handleImageSelect(image.Key)}
                  onClick={e => e.stopPropagation()}
                />
              </div>
              <div className="p-2 bg-gray-100 text-gray-800 text-xs font-medium text-center">
                {image.timestamp ? `${image.timestamp.date} ${image.timestamp.time}` : 'No Date'}
              </div>
              <AspectRatio ratio={16 / 9}>
                {thumbnailsUrls[image.Key] ? (
                  <img
                    src={thumbnailsUrls[image.Key]}
                    alt={image.Key}
                    className="object-cover w-full h-full"
                    loading="lazy"
                    onError={e => {
                      console.error('Image load error:', image.Key);
                      e.target.src = ''; // Clear src on error
                    }}
                  />
                ) : (
                  <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                    <p className="text-sm text-gray-500">Loading...</p>
                  </div>
                )}
              </AspectRatio>
              <div className="p-2">
                <p className="text-sm truncate">{image.Key.split('/').pop()}</p>
                <p className="text-xs text-gray-500">
                  {assignedImageKeys.includes(image.Key) ? 'Assigned' : 'Unassigned'}
                </p>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default FileManager;
